import {css} from 'styled-components';
import {focusOutlineColor, focusOutlineWidth, focusOutlineStyle, focusOutlineOffset} from '@nib-components/theme';
import * as config from './variables';
import chevronIcon from '../assets/chevron';

/* ----------------------------------------------------------------- 
           ______________
       /.--------------.\
      //                \\
     //                  \\
    || .-..----. .-. .--. ||
    ||( ( '-..-'|.-.||.-.|||
    || \ \  ||  || ||||_||||
    ||._) ) ||  \'-'/||-' ||
     \\'-'  `'   `-' `'  //
      \\                //
       \\______________//
        '--------------'
              |_|_
       ____ _/ _)_)
           '  | (_)
        .--'"\| ()
      mx      | |
              | |
              |_|  

  You probably don't need to edit this file!

  This is the css provided by react-datepicker and truth be told it 
  is complex and hard to understand in places. 

  Variables have been pulled out to src/style/variables.js

  And the majority of those are being pulled from our theme to enable
  whitelabelling.

/* ----------------------------------------------------------------- */

const style = css`
  .react-datepicker {
    font-family: ${config.fontFamily};
    font-size: ${config.fontSize};
    background-color: ${config.whiteColor};
    color: ${config.textColor};
    border: 1px solid ${config.borderColor};
    border-radius: ${config.borderRadius};
    position: relative;
  }

  .react-datepicker__triangle {
    display: none;
  }

  .react-datepicker--time-only {
    .react-datepicker__time-container {
      border-left: 0;
    }

    .react-datepicker__time {
      border-radius: 0.3rem;
    }

    .react-datepicker__time-box {
      border-radius: 0.3rem;
    }
  }

  .react-datepicker-popper {
    z-index: 2;
    line-height: 0;
    background-color: ${config.whiteColor};
    box-shadow: 0px 2px 4px 0 rgba(203, 203, 203, 0.5);
    padding: 8px;
  }

  .react-datepicker__header {
    text-align: center;
    line-height: 1.5;
    line-height: ${config.lineHeight};
    background-color: ${config.backgroundColor};
    border-top-left-radius: ${config.borderRadius};
    border-top-right-radius: ${config.borderRadius};
    padding-top: 8px;
    position: relative;

    &--time {
      padding-bottom: 8px;
      padding-left: 5px;
      padding-right: 5px;
    }
  }

  .react-datepicker__header__dropdown {
    margin-top: 5px;
  }

  .react-datepicker__year-dropdown-container--select,
  .react-datepicker__month-dropdown-container--select,
  .react-datepicker__month-year-dropdown-container--select,
  .react-datepicker__year-dropdown-container--scroll,
  .react-datepicker__month-dropdown-container--scroll,
  .react-datepicker__month-year-dropdown-container--scroll {
    display: inline-block;
    margin: 0 2px;
  }

  .react-datepicker__current-month,
  .react-datepicker-time__header {
    margin-top: 0;
    color: ${config.headerColor};
    font-weight: bold;
    font-size: calc(${config.fontSize} * 1.18);
  }

  .react-datepicker-time__header {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  .react-datepicker__navigation {
    display: block;
    background-color: transparent;
    background-image: url(${chevronIcon});
    background-position: center;
    background-size: 16px;
    background-repeat: no-repeat;
    border: 0;
    border-radius: 50%;
    padding: 0;
    cursor: pointer;
    position: absolute;
    top: 0;
    width: 40px;
    height: 40px;
    z-index: 1;
    text-indent: -999em;
    transform: rotate(270deg);
    overflow: hidden;

    &:hover {
      background-color: ${config.dayBackgroundHoverColor};
    }

    &:focus {
      outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
      outline-offset: ${focusOutlineOffset};
    }

    &--previous {
      left: 0;
      transform: rotate(90deg);

      &--disabled,
      &--disabled:hover {
        opacity: 0.3;
        cursor: default;
      }
    }

    &--next {
      right: 0;
      &--with-time:not(&--with-today-button) {
        right: 80px;
      }

      &--disabled,
      &--disabled:hover {
        opacity: 0.3;
        cursor: default;
      }
    }

    &--years {
      position: relative;
      top: 0;
      display: block;
      margin-left: auto;
      margin-right: auto;

      &-previous {
        top: 4px;
        border-top-color: ${config.mutedColor};

        &:hover {
          border-top-color: ${config.selectedColor};
        }
      }

      &-upcoming {
        top: -4px;
        border-bottom-color: ${config.mutedColor};

        &:hover {
          border-bottom-color: ${config.selectedColor};
        }
      }
    }
  }

  .react-datepicker__month-container {
    float: left;
  }

  .react-datepicker__month {
    text-align: center;
  }

  .react-datepicker__time-container {
    float: right;
    border-left: 1px solid ${config.borderColor};
    width: 70px;

    &--with-today-button {
      display: inline;
      border: 1px solid #aeaeae;
      border-radius: 0.3rem;
      position: absolute;
      right: -72px;
      top: 0;
    }

    .react-datepicker__time {
      position: relative;
      background: ${config.whiteColor};

      .react-datepicker__time-box {
        width: 70px;
        overflow-x: hidden;
        margin: 0 auto;
        text-align: center;
        ul.react-datepicker__time-list {
          list-style: none;
          margin: 0;
          height: calc(195px + (#{${config.itemSize}} / 2));
          overflow-y: scroll;
          padding-right: 30px;
          width: 100%;
          box-sizing: content-box;

          li.react-datepicker__time-list-item {
            padding: 5px 10px;
            &:hover {
              cursor: pointer;
              background-color: ${config.backgroundColor};
            }
            &--selected {
              background-color: ${config.selectedColor};
              color: ${config.whiteColor};
              font-weight: bold;
              &:hover {
                background-color: ${config.selectedColor};
              }
            }
            &--disabled {
              color: ${config.mutedColor};

              &:hover {
                cursor: default;
                background-color: transparent;
              }
            }
          }
        }
      }
    }
  }

  .react-datepicker__week-number {
    color: ${config.mutedColor};
    display: inline-block;
    width: ${config.itemSize};
    line-height: ${config.itemSize};
    text-align: center;
    margin: ${config.dayMargin};
    &.react-datepicker__week-number--clickable {
      cursor: pointer;
      &:hover {
        border-radius: ${config.borderRadius};
        background-color: ${config.backgroundColor};
      }
    }
  }

  .react-datepicker__day-names {
    font-weight: bold;
  }

  .react-datepicker__day-names,
  .react-datepicker__week {
    white-space: nowrap;
  }

  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    color: ${config.textColor};
    display: inline-block;
    width: ${config.itemSize};
    line-height: ${config.itemSize};
    text-align: center;
    margin: ${config.dayMargin};
    background-color: ${config.dayBackgroundColor};
  }

  .react-datepicker__day {
    cursor: pointer;

    &:hover {
      border-radius: ${config.borderRadius};
      background-color: ${config.dayBackgroundHoverColor};
    }

    &--highlighted {
      border-radius: ${config.borderRadius};
      background-color: ${config.highlightedColor};
      color: ${config.whiteColor};

      &:hover {
      }
    }

    &--selected,
    &--in-selecting-range,
    &--in-range {
      border-radius: ${config.borderRadius};
      background-color: ${config.selectedColor};
      color: ${config.whiteColor};

      &:hover {
        background-color: ${config.darkSelectedColor};
      }
    }

    &--keyboard-selected {
      position: relative;
      border-radius: ${config.borderRadius};
      background-color: ${config.focusedColor};
      color: ${config.whiteColor};

      outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
      outline-offset: ${focusOutlineOffset};

      &:hover {
        background-color: ${config.darkSelectedColor};
      }
    }

    &--in-selecting-range:not(.react-datepicker__day--in-range) {
      background-color: ${config.focusedColor};
    }

    &--disabled {
      cursor: no-drop;
      color: ${config.mutedColor};
      background-color: transparent;

      &:hover {
        background-color: transparent;
      }
    }
  }

  .react-datepicker__month--selecting-range .react-datepicker__day--in-range:not(.react-datepicker__day--in-selecting-range) {
    background-color: ${config.focusedColor};
  }

  .react-datepicker__input-container {
    position: relative;
  }

  .react-datepicker__year-read-view,
  .react-datepicker__month-read-view,
  .react-datepicker__month-year-read-view {
    border: 1px solid transparent;
    border-radius: ${config.borderRadius};

    &:hover {
      cursor: pointer;

      .react-datepicker__year-read-view--down-arrow,
      .react-datepicker__month-read-view--down-arrow {
        border-top-color: ${config.mutedColor};
      }
    }
  }

  .react-datepicker__year-dropdown,
  .react-datepicker__month-dropdown,
  .react-datepicker__month-year-dropdown {
    background-color: ${config.backgroundColor};
    position: absolute;
    width: 50%;
    left: 25%;
    top: 30px;
    z-index: 1;
    text-align: center;
    border-radius: ${config.borderRadius};
    border: 1px solid ${config.borderColor};

    &:hover {
      cursor: pointer;
    }

    &--scrollable {
      height: 150px;
      overflow-y: scroll;
    }
  }

  .react-datepicker__year-option,
  .react-datepicker__month-option,
  .react-datepicker__month-year-option {
    line-height: 20px;
    width: 100%;
    display: block;
    margin-left: auto;
    margin-right: auto;

    &:first-of-type {
      border-top-left-radius: ${config.borderRadius};
      border-top-right-radius: ${config.borderRadius};
    }

    &:last-of-type {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      border-bottom-left-radius: ${config.borderRadius};
      border-bottom-right-radius: ${config.borderRadius};
    }

    &:hover {
      background-color: ${config.mutedColor};

      .react-datepicker__navigation--years-upcoming {
        border-bottom-color: ${config.mutedColor};
      }

      .react-datepicker__navigation--years-previous {
        border-top-color: ${config.mutedColor};
      }
    }

    &--selected {
      position: absolute;
      left: 15px;
    }
  }

  .react-datepicker__close-icon {
    background-color: transparent;
    border: 0;
    cursor: pointer;
    display: inline-block;
    height: 0;
    outline: 0;
    padding: 0;
    vertical-align: middle;

    &::after {
      background-color: ${config.selectedColor};
      border-radius: 50%;
      bottom: 0;
      box-sizing: border-box;
      color: ${config.whiteColor};
      content: '\\00d7';
      cursor: pointer;
      font-size: 12px;
      height: 16px;
      width: 16px;
      line-height: 1;
      margin: -8px auto 0;
      padding: 2px;
      position: absolute;
      right: 7px;
      text-align: center;
      top: 50%;
    }
  }

  .react-datepicker__today-button {
    background: ${config.backgroundColor};
    border-top: 1px solid ${config.borderColor};
    cursor: pointer;
    text-align: center;
    font-weight: bold;
    line-height: ${config.lineHeight};
    line-height: 1.5;
    padding: 5px 0;
    clear: left;
  }

  .react-datepicker__portal {
    position: fixed;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.8);
    left: 0;
    top: 0;
    justify-content: center;
    align-items: center;
    display: flex;
    z-index: 2147483647;

    .react-datepicker__day-name,
    .react-datepicker__day,
    .react-datepicker__time-name {
      width: 3rem;
      line-height: 3rem;
    }

    // Resize for small screens
    @media (max-width: 400px), (max-height: 550px) {
      .react-datepicker__day-name,
      .react-datepicker__day,
      .react-datepicker__time-name {
        width: 2rem;
        line-height: 2rem;
      }
    }

    .react-datepicker__current-month,
    .react-datepicker-time__header {
      font-size: calc(${config.fontSize} * 1.8);
    }

    .react-datepicker__navigation {
      border: 1.8 * ${config.navigationSize} solid transparent;
    }

    .react-datepicker__navigation--previous {
      border-right-color: ${config.mutedColor};

      &:hover {
        border-right-color: ${config.mutedColor};
      }

      &--disabled,
      &--disabled:hover {
        border-right-color: ${config.navigationDisabledColor};
        cursor: no-drop;
      }
    }

    .react-datepicker__navigation--next {
      border-left-color: ${config.mutedColor};

      &:hover {
        border-left-color: ${config.mutedColor};
      }

      &--disabled,
      &--disabled:hover {
        border-left-color: ${config.navigationDisabledColor};
        cursor: no-drop;
      }
    }
  }
`;

export default style;
